import React, { useEffect, useMemo, useState } from 'react';
import TIMEZONES, { TIMEZONE_GROUPS, TIMEZONE_GROUP_LABELS, getTzLabel } from '../data/timezones';

/**
 * Choosing where you are.
 *
 * This was a native <select size={9}> — a grey scrolling list box, fifty lines
 * long, with the regions as optgroup headings the phone renders however it
 * likes. Silvia: the names look disorganised and there seem to be more of them
 * than there are time zones. Both true, and neither was really about the names.
 *
 *   the list box    became a grid of buttons, the same one the languages use,
 *                   without the flags. Regions are headings you can see rather
 *                   than something the widget decides how to draw.
 *   fifty names     for eighteen distinct offsets is not padding: Amsterdam and
 *                   Berlin keep the same clock today and there is no promise
 *                   they will next year, and somebody looking for their own
 *                   city needs to find their own city. So the list stays, but
 *                   each button carries the time it is there right now — which
 *                   is what actually tells you whether you have found yourself.
 *
 * The clock ticks only while the panel is open. A picker nobody is looking at
 * does not need to know the time.
 */
export default function TimezonePicker({ value, onChange, language, ariaLabel }) {
  // Re-render on the minute so the times shown are the times it is.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const timeIn = useMemo(() => {
    const now = new Date();
    const cache = {};
    return (tz) => {
      if (cache[tz] !== undefined) return cache[tz];
      try {
        cache[tz] = new Intl.DateTimeFormat(language || 'it', {
          timeZone: tz, hour: '2-digit', minute: '2-digit',
        }).format(now);
      } catch (_) {
        // An unknown zone must not take the whole picker down with it.
        cache[tz] = '';
      }
      return cache[tz];
    };
  }, [language, tick]);

  // A zone saved on the account that is not one of ours — somebody else's
  // phone, or a name we have since dropped. It stays choosable, at the top,
  // rather than silently vanishing from the list it belongs to.
  const unknown = value && !TIMEZONES.find(t => t.value === value) ? value : null;

  function cell(tz, label, sub) {
    const active = value === tz;
    return (
      <button
        key={tz}
        type="button"
        onClick={() => onChange(tz)}
        aria-pressed={active}
        aria-label={`${label}${sub ? ', ' + sub : ''}`}
        className="tap-grow"
        style={{
          '--tap-x': '4px',
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
          minHeight: 46, padding: '7px 10px',
          fontFamily: 'inherit', fontSize: '0.88rem',
          fontWeight: active ? 800 : 500,
          textAlign: 'start', cursor: 'pointer',
          borderRadius: 10,
          background: active ? 'var(--color-primary-light)' : 'var(--color-card)',
          color: 'var(--color-text)',
          border: active ? '2px solid var(--color-primary)' : '1.5px solid var(--color-border)',
        }}
      >
        <span style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        {sub && (
          <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {sub}
          </span>
        )}
      </button>
    );
  }

  const grid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
    gap: 8,
  };

  return (
    <div role="group" aria-label={ariaLabel} style={{ maxHeight: 340, overflowY: 'auto' }}>
      {unknown && (
        <div style={{ ...grid, marginBottom: 12 }}>
          {cell(unknown, unknown, timeIn(unknown))}
        </div>
      )}
      {TIMEZONE_GROUPS.map(group => {
        const zones = TIMEZONES.filter(tz => tz.group === group);
        if (!zones.length) return null;
        return (
          <div key={group} style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em',
              textTransform: 'uppercase', color: 'var(--color-text-muted)',
              margin: '0 0 6px',
            }}>
              {TIMEZONE_GROUP_LABELS[language]?.[group] || group}
            </div>
            <div style={grid}>
              {zones.map(tz => cell(
                tz.value,
                getTzLabel(tz, language).replace(/^\u{1F550}\s*/u, ''),
                timeIn(tz.value),
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
